/** Bilanzkarte (1200 × 630) als PNG, im Stil des Spiels. Wird im Browser gezeichnet, nichts geht an einen Server. */
import { MEDAL } from './medals';
import type { Era } from './medals';

const W = 1200, H = 630;
const DIFF: Record<string, string> = { leicht: 'Leicht', normal: 'Normal', schwer: 'Schwer' };

function img(src: string): Promise<HTMLImageElement | null> {
  return new Promise((ok) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => ok(null); i.src = src; });
}
function hex(c: CanvasRenderingContext2D, x: number, y: number, r: number) {
  c.beginPath();
  [[0, -1], [.87, -.5], [.87, .5], [0, 1], [-.87, .5], [-.87, -.5]].forEach(([a, b], i) => (i ? c.lineTo(x + a * r, y + b * r) : c.moveTo(x + a * r, y + b * r)));
  c.closePath();
}
function gold(c: CanvasRenderingContext2D, x: number, y: number, r: number) {
  const g = c.createLinearGradient(x - r, y - r, x + r, y + r); g.addColorStop(0, '#f0cc7a'); g.addColorStop(.55, '#d6aa4e'); g.addColorStop(1, '#8c6a24'); return g;
}
/** Text umbrechen, gibt die Zeilen zurück. */
function wrap(c: CanvasRenderingContext2D, t: string, w: number): string[] {
  const out: string[] = []; let line = '';
  for (const word of t.split(' ')) { const n = line ? line + ' ' + word : word; if (c.measureText(n).width > w && line) { out.push(line); line = word; } else line = n; }
  if (line) out.push(line); return out;
}

export async function drawCard(o: { party: string; color: string; era: Era; medals: string[]; diff: string }): Promise<Blob | null> {
  if (typeof document === 'undefined') return null;
  try { await Promise.all(['700 80px "Barlow Condensed"', '600 26px "Barlow Condensed"', '400 24px "Barlow"', '600 24px "JetBrains Mono"'].map((f) => document.fonts.load(f))); } catch { /* Systemschrift */ }
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const c = cv.getContext('2d'); if (!c) return null;
  c.fillStyle = '#0e1215'; c.fillRect(0, 0, W, H);
  const bg = await img(o.era.over ? '/img/szene/bilanz.webp' : '/img/moment/wahlsieg.webp');
  if (bg) { const s = H / bg.height, w = bg.width * s; c.drawImage(bg, W - w + 80, 0, w, H); }
  const fade = c.createLinearGradient(0, 0, W, 0);
  fade.addColorStop(0, '#0e1215'); fade.addColorStop(.42, 'rgba(14,18,21,.96)'); fade.addColorStop(.78, 'rgba(14,18,21,.35)'); fade.addColorStop(1, 'rgba(14,18,21,.15)');
  c.fillStyle = fade; c.fillRect(0, 0, W, H);
  c.fillStyle = '#d6aa4e'; c.fillRect(0, 0, 360, 6); c.fillStyle = '#2b353b'; c.fillRect(360, 0, W - 360, 6);

  // Marke
  hex(c, 96, 78, 26); c.fillStyle = gold(c, 96, 78, 26); c.fill();
  c.fillStyle = '#1a1206'; c.font = '700 26px "Barlow Condensed", sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText('K', 96, 80);
  c.textAlign = 'left'; c.fillStyle = '#ece6d8'; c.font = '700 34px "Barlow Condensed", sans-serif'; c.fillText('KABINETT', 136, 80);
  c.font = '600 20px "Barlow Condensed", sans-serif'; c.fillStyle = '#97a19c'; c.textAlign = 'right'; c.fillText(`SCHWIERIGKEIT: ${(DIFF[o.diff] || 'Normal').toUpperCase()}`, W - 56, 80); c.textAlign = 'left';

  // Partei
  c.fillStyle = o.color; c.fillRect(72, 150, 18, 18);
  c.fillStyle = '#ece6d8'; c.font = '600 26px "Barlow Condensed", sans-serif'; c.textBaseline = 'alphabetic'; c.fillText(o.party.toUpperCase(), 102, 168);

  // Beiname
  c.font = '700 80px "Barlow Condensed", sans-serif'; c.fillStyle = '#ece6d8';
  const lines = wrap(c, o.era.title.toUpperCase(), 640).slice(0, 2);
  lines.forEach((l, i) => c.fillText(l, 70, 258 + i * 80));
  let y = 258 + (lines.length - 1) * 80 + 54;

  // Zahlen
  c.font = '600 24px "JetBrains Mono", monospace'; c.fillStyle = '#d6aa4e';
  const stats = [o.era.years, `${o.era.legs} ${o.era.legs === 1 ? 'Legislatur' : 'Legislaturen'}`, `${o.era.laws} Gesetze`, ...(o.era.share != null ? [`${o.era.share.toFixed(1).replace('.', ',')} %`] : [])];
  c.fillText(stats.join('  ·  '), 72, y); y += 40;

  // Orden
  const ms = o.medals.filter((id) => MEDAL[id]).slice(0, 6);
  const pics = await Promise.all(ms.map((id) => img(`/img/orden/${id}.webp`)));
  if (ms.length) {
    y += 40;
    ms.forEach((id, i) => {
      const x = 104 + i * 104, m = MEDAL[id];
      hex(c, x, y, 34); c.fillStyle = gold(c, x, y, 34); c.fill();
      const pic = pics[i];
      if (pic) { c.save(); hex(c, x, y, 31); c.clip(); c.drawImage(pic, x - 31, y - 31, 62, 62); c.restore(); }
      else { c.fillStyle = '#1a1206'; c.font = `700 ${m.sym.length > 2 ? 20 : 26}px "Barlow Condensed", sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle'; c.fillText(m.sym, x, y + 2); }
      c.fillStyle = '#cfc9ba'; c.font = '600 15px "Barlow Condensed", sans-serif'; c.textBaseline = 'alphabetic';
      wrap(c, m.n.toUpperCase(), 96).slice(0, 2).forEach((l, k) => c.fillText(l, x, y + 58 + k * 17));
      c.textAlign = 'left';
    });
  }
  c.font = '600 22px "Barlow Condensed", sans-serif'; c.fillStyle = '#d6aa4e'; c.textBaseline = 'alphabetic';
  c.fillText('KABINETT-SPIEL.DE', 72, H - 40);
  c.fillStyle = '#97a19c'; c.font = '400 18px "Barlow", sans-serif'; c.fillText('Wie lange hält deine Regierung?', 270, H - 40);
  return new Promise((ok) => cv.toBlob((b) => ok(b), 'image/png'));
}
